import checkSessionsOverlap from 'features/Reserve/utils/checkSessionsOverlap';
import useCartItems from 'hooks/useCartItems';
import moment from 'moment';
import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { isAtRoute } from 'router/routeMatch';
import getMapMedia from 'utils/data/medias/getMapMedia';
import { MINUTES_PER_DAY } from 'utils/date/minutesPerDay';
import useResourceBookingSessionPropsFromUrl from './useResourceBookingSessionPropsFromUrl';
import useResourceBookingSessionSelectedData from './useResourceBookingSessionSelectedData';
import { getSessionTimeOptions } from './useSessionTimeOptions';

interface BookingProps<D> {
  collectionId?: string;
  groupId?: string;
  resourceId?: string;
  date?: string;
  time?: string;
  latest?: boolean;
  duration?: D;
}

const formatNextBookingUrl = (props: BookingProps<string>) => {
  let url = '';
  if (props.collectionId) {
    url += `/collection/${props.collectionId}`;
    if (props.groupId) {
      url += `/group/${props.groupId}`;
      if (props.resourceId) {
        url += `/resource/${props.resourceId}`;
      }
    }

    if (props.date || props.time || props.duration) {
      const searchParams = new URLSearchParams();
      if (props.date) {
        searchParams.set('date', props.date);
      }
      if (props.time) {
        searchParams.set('time', props.time);
      }
      if (props.duration) {
        searchParams.set('duration', props.duration);
      }
      url += `?${searchParams.toString()}`;
      if (props.latest) {
        url += '&latest=true';
      }
    } else if (props.latest) {
      url += '?latest=true';
    }
  }
  return url;
};

const useResourceBookingSession = () => {
  const location = useLocation();
  const history = useHistory();

  /* eslint-disable prefer-const */
  // 1 - get props from url
  let { collectionId, groupId, resourceId, date, time, duration, latest } =
    useResourceBookingSessionPropsFromUrl();

  // 2 - get data using props from url
  let {
    firstAvailableDate,
    isInitialLoading,
    isLoading,
    isLoadingListing,
    isLoadingSessions,
    isLoadingCancellationPolicy,
    listing,
    listingError,
    resourceGroup,
    resource,
    sessions,
    selectedSession,
    availabilityRule,
    continuousSelection,
    isContinuous,
    timeFrame,
    cancellationPolicy,
  } = useResourceBookingSessionSelectedData({
    collectionId,
    groupId,
    resourceId,
    date,
    time,
    duration,
    latest,
  });
  /* eslint-enable prefer-const */

  const { cartItems } = useCartItems();
  const resourcesInCart = Object.values(cartItems);

  // Get group-specific first available date if groupId is set
  const groupFirstAvailableDate = groupId
    ? firstAvailableDate?.resourceGroups?.find((g) => g.id === groupId)
        ?.firstAvailableDate?.scheduledDate
    : undefined;

  // Use group-specific date if available, otherwise fall back to collection-level
  const firstAvailableScheduledDate =
    groupFirstAvailableDate ?? firstAvailableDate?.firstAvailableDate;

  // 3 - if the given ids are not found, reset the data
  if (!listing) {
    collectionId = undefined;
    groupId = undefined;
    resourceId = undefined;
    date = undefined;
    time = undefined;
    duration = undefined;
    sessions = [];
    selectedSession = undefined;
  } else if (date) {
    // Only override if the user hasn't explicitly selected a date
    // AND the first available is after their selection
    // AND we're not on a group page (where null means no availability)
    if (
      !isLoadingSessions &&
      sessions.length === 0 &&
      firstAvailableScheduledDate &&
      moment(firstAvailableScheduledDate).isAfter(date)
    ) {
      // If we're on a group page and group-specific firstAvailable is null,
      // don't override - it means no availability for this group
      if (!groupId || groupFirstAvailableDate) {
        date = firstAvailableScheduledDate;
      }
    }
  } else if (firstAvailableScheduledDate) {
    date = firstAvailableScheduledDate;
  } else {
    date = moment().format('YYYY-MM-DD');
  }

  if (!resourceGroup) {
    groupId = undefined;
    resourceId = undefined;
    selectedSession = undefined;
    time = undefined;
    duration = undefined;
  } else {
    groupId = resourceGroup.id;
  }
  if (!resource) {
    resourceId = undefined;
  } else {
    resourceId = resource.id;
  }

  // 4 - make sure that what we have already put in the cart is not available anymore, to prevent double booking
  if (listing && isContinuous) {
    // A free span of a resource the cart already holds for an overlapping time is withdrawn whole.
    // Coarser than carving the cart's booking out of it, but a guest rarely books one resource twice
    // on one day, and it can never offer a time the cart has taken.
    sessions = sessions.filter(
      (span) =>
        !resourcesInCart.find(
          (cartItem) =>
            cartItem.experienceId === span.experienceId &&
            checkSessionsOverlap(
              cartItem.sessionDateTime,
              moment.duration(cartItem.sessionDuration).asMinutes(),
              span.startDateTime,
              span.duration,
            ),
        ),
    );
  } else if (listing) {
    if (getMapMedia(listing.medias)) {
      sessions = sessions.filter(
        (session) =>
          !resourcesInCart.find(
            (cartItem) =>
              cartItem.experienceId === session.experienceId &&
              checkSessionsOverlap(
                cartItem.sessionDateTime,
                moment.duration(cartItem.sessionDuration).asMinutes(),
                session.startDateTime,
                session.duration,
              ),
          ),
      );
    } else if (resourceGroup) {
      const sessionTimeOptions = getSessionTimeOptions(sessions);
      resourcesInCart.forEach((cartItem) => {
        if (resourceGroup && resourceGroup.id === cartItem.groupId) {
          cartItem.participants.forEach(() => {
            sessionTimeOptions.forEach((sessionTimeOption) => {
              const firstResouceSessionInCartIndex = sessions.findIndex(
                (session) =>
                  resourceGroup &&
                  !!resourceGroup.experiences.find(
                    (exp) => exp.id === session.experienceId,
                  ) &&
                  session.startDateTime ===
                    sessionTimeOption.original.startDateTime &&
                  session.duration === sessionTimeOption.original.duration &&
                  checkSessionsOverlap(
                    cartItem.sessionDateTime,
                    moment.duration(cartItem.sessionDuration).asMinutes(),
                    session.startDateTime,
                    session.duration,
                  ),
              );
              if (firstResouceSessionInCartIndex >= 0) {
                sessions.splice(firstResouceSessionInCartIndex, 1);
              }
            });
          });
        }
      });
    }
  }

  // 5 - time and duration must be coherent with what's available in sessions[]
  if (isContinuous) {
    // The URL carries the guest's choice as the picker made it: a start and a length for a flexible
    // resource, a count of days or nights as whole days for a day-based one. The session's own start
    // and length are what that choice books, which for a stay includes the start and end times.
    if (continuousSelection) {
      time = continuousSelection.units
        ? undefined
        : continuousSelection.startDateTime.split('T')[1];
      duration = continuousSelection.units
        ? continuousSelection.units * MINUTES_PER_DAY
        : continuousSelection.durationMinutes;
    } else {
      time = undefined;
      duration = undefined;
    }
  } else if (selectedSession) {
    time = selectedSession.startDateTime.split('T')[1];
    duration = selectedSession.duration;
  } else {
    if (time && !sessions.find((s) => s.startDateTime.split('T')[1] === time)) {
      time = undefined;
    }
    if (duration && !sessions.find((s) => s.duration === duration)) {
      duration = undefined;
    }
    if (
      time &&
      duration &&
      !sessions.find(
        (s) =>
          s.startDateTime.split('T')[1] === time && s.duration === duration,
      )
    ) {
      time = undefined;
      duration = undefined;
    }

    // preselect time and duration when opening the group page
    if (resourceGroup && !time && !duration) {
      const sessionsOptions = getSessionTimeOptions(sessions);
      time = sessionsOptions[0]?.original.startDateTime.split('T')[1];
      duration = sessionsOptions[0]?.original.duration;
    }

    // preset time and duration if only one option is available
    if (!time) {
      const allAvailableTimes = new Set(
        sessions.map((s) => s.startDateTime.split('T')[1]),
      );
      if (allAvailableTimes.size === 1) {
        time = allAvailableTimes.values().next().value;
      }
    }
    if (!duration) {
      const allAvailableDurations = new Set(sessions.map((e) => e.duration));
      if (allAvailableDurations.size === 1) {
        duration = allAvailableDurations.values().next().value;
      }
    }
  }

  // Once the listing resolves, prefer its slug for the collection segment so the URL the page
  // normalizes to (via cleanedUrl below) and every param change stay keyword-rich. This is the
  // UUID→slug swap: a UUID (or missed-slug) entry gets rewritten to the slug on load. Falls back
  // to the raw route value when the listing has no slug yet.
  const collectionRouteId = listing?.slug ?? collectionId;

  // 6 - format new cleaned url
  const cleanedUrl = formatNextBookingUrl({
    collectionId: collectionRouteId,
    groupId,
    resourceId,
    date,
    time,
    latest,
    duration: duration
      ? moment.duration(duration, 'minutes').toISOString()
      : undefined,
  });

  const setResourceBookingSessionParams = useCallback(
    (changes: BookingProps<number>) => {
      const nextUrl = formatNextBookingUrl({
        collectionId: collectionRouteId,
        groupId,
        resourceId,
        date,
        time,
        latest,
        ...changes,
        duration: moment
          .duration(changes.duration || duration || 0, 'minutes')
          .toISOString(),
      });

      if (!isAtRoute(location, nextUrl)) {
        const [pathname, search] = nextUrl.split('?');
        if (location.pathname !== pathname) {
          history.push({
            pathname,
            search,
          });
        } else {
          history.replace({
            pathname,
            search,
          });
        }
      }
    },
    [
      location,
      history,
      collectionRouteId,
      groupId,
      resourceId,
      date,
      time,
      latest,
      duration,
    ],
  );

  return {
    firstAvailableDate,
    isInitialLoading,
    isLoading,
    isLoadingListing,
    isLoadingSessions,
    isLoadingCancellationPolicy,
    collection: listing,
    listingError,
    cleanedUrl,
    listing,
    resourceGroup,
    resource,
    sessions,
    selectedSession,
    availabilityRule,
    continuousSelection,
    isContinuous,
    timeFrame,
    cancellationPolicy,
    collectionId,
    groupId,
    resourceId,
    date,
    time,
    duration,
    latest,
    setResourceBookingSessionParams,
  };
};

export default useResourceBookingSession;
